import React, { useState, useEffect } from 'react';
import './OverheadsTab.css';
import { businessService } from '../services';
import { Button, TextField, Select, MenuItem, FormControl, InputLabel, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

const REGULARITY_OPTIONS = [
  { value: 'monthly', label: 'Monthly', multiplier: 12 },
  { value: 'quarterly', label: 'Quarterly', multiplier: 4 },
  { value: 'annually', label: 'Annually', multiplier: 1 },
  { value: 'weekly', label: 'Weekly', multiplier: 52 }
];

const EXPENSE_TYPES = [
  { id: 1, name: 'Accountancy & Legal' },
  { id: 2, name: 'Bank Charges & Interest' },
  { id: 3, name: 'Card Machine Fees' },
  { id: 4, name: 'Client Comfort' },
  { id: 5, name: 'Communications' },
  { id: 6, name: 'Electric' },
  { id: 7, name: 'Furniture & Equipment' },
  { id: 8, name: 'Gas' },
  { id: 9, name: 'Insurance' },
  { id: 10, name: 'IT & Software' },
  { id: 11, name: 'Leasing and Finance' },
  { id: 12, name: 'Licences' },
  { id: 13, name: 'Loan Repayment' },
  { id: 14, name: 'Maintenance & Repairs' },
  { id: 15, name: 'Marketing & Advertising' },
  { id: 16, name: 'Office Supplies' },
  { id: 17, name: '⚠️ Payroll (use Staff tab instead)' },
  { id: 18, name: 'Personal' },
  { id: 19, name: 'Postage and Courier' },
  { id: 20, name: 'Rates' },
  { id: 21, name: 'Rent' },
  { id: 22, name: '⚠️ Stock (use Materials instead)' },
  { id: 23, name: 'Subscriptions' },
  { id: 24, name: 'Sundry' },
  { id: 25, name: 'Taxation' },
  { id: 26, name: 'Training' },
  { id: 27, name: 'Waste Disposal' },
  { id: 28, name: 'Water' }
];

const OverheadsTab = ({ onTotalChange }) => {
  const [outgoings, setOutgoings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newOutgoing, setNewOutgoing] = useState({
    name: '',
    amount: '',
    type_id: 1,
    regularity: 'monthly'
  });

  // Fetch outgoings on component mount
  useEffect(() => {
    fetchOutgoings();
  }, []);

  // Calculate total annual cost when outgoings change
  const [totalAnnualCost, setTotalAnnualCost] = useState(0);
  
  useEffect(() => {
    // Calculate total, excluding Payroll (17) and Stock (22) to prevent double-counting
    // Payroll is already accounted for in Staff tab hourly rates
    // Stock is already accounted for in Materials per service
    const total = outgoings
      .filter(outgoing => {
        const typeId = parseInt(outgoing.type_id);
        // Exclude Payroll (17) and Stock (22) from overhead calculation
        return typeId !== 17 && typeId !== 22;
      })
      .reduce((sum, outgoing) => {
        const amount = parseFloat(outgoing.amount) || 0;
        const regularity = outgoing.regularity || 'monthly';
        const multiplier = REGULARITY_OPTIONS.find(option => option.value === regularity)?.multiplier || 12;
        return sum + (amount * multiplier);
      }, 0);
    
    setTotalAnnualCost(total);
    
    // Call the onTotalChange callback if provided
    if (onTotalChange) {
      onTotalChange(total);
    }
  }, [outgoings, onTotalChange]);

  const fetchOutgoings = async () => {
    try {
      setLoading(true);
      const response = await businessService.getAllOutgoings();
      
      // Transform the data to include regularity (default to monthly)
      const transformedOutgoings = response.data.map(outgoing => ({
        ...outgoing,
        regularity: outgoing.regularity || 'monthly'
      }));
      
      setOutgoings(transformedOutgoings);
      setError(null);
    } catch (err) {
      console.error('Error fetching outgoings:', err);
      setError('Failed to load outgoings. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const calculateAnnualCost = (amount, regularity) => {
    const amountValue = parseFloat(amount) || 0;
    const multiplier = REGULARITY_OPTIONS.find(option => option.value === regularity)?.multiplier || 12;
    return amountValue * multiplier;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewOutgoing(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRegularityChange = (outgoingId, regularity) => {
    // Update in local state only - doesn't save to backend since field doesn't exist
    setOutgoings(prevOutgoings => 
      prevOutgoings.map(outgoing => 
        outgoing.id === outgoingId 
          ? { ...outgoing, regularity } 
          : outgoing
      )
    );
  };

  const handleAmountChange = async (outgoingId, amount) => {
    try {
      // Update in local state
      setOutgoings(prevOutgoings => 
        prevOutgoings.map(outgoing => 
          outgoing.id === outgoingId 
            ? { ...outgoing, amount } 
            : outgoing
        )
      );
      
      // Update in backend
      const outgoing = outgoings.find(o => o.id === outgoingId);
      if (outgoing) {
        await businessService.updateOutgoing(outgoingId, {
          ...outgoing,
          amount
        });
      }
    } catch (error) {
      console.error('Error updating outgoing amount:', error);
      alert('Failed to update amount. Please try again.');
      fetchOutgoings(); // Refresh data from server
    }
  };

  const addOutgoing = async () => {
    if (!newOutgoing.name || !newOutgoing.amount) {
      alert('Please enter both a name and amount for the outgoing.');
      return;
    }
    
    try {
      setLoading(true);
      const response = await businessService.createOutgoing({
        name: newOutgoing.name,
        amount: parseFloat(newOutgoing.amount),
        type_id: parseInt(newOutgoing.type_id)
      });
      
      // Add the new outgoing with regularity to the list
      setOutgoings([
        ...outgoings, 
        { 
          ...response.data, 
          regularity: newOutgoing.regularity 
        }
      ]);
      
      // Reset the form
      setNewOutgoing({
        name: '',
        amount: '',
        type_id: 1,
        regularity: 'monthly'
      });
    } catch (error) {
      console.error('Error adding outgoing:', error);
      alert('Failed to add outgoing. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const deleteOutgoing = async (id) => {
    if (!window.confirm('Are you sure you want to delete this outgoing?')) {
      return;
    }
    
    try {
      setLoading(true);
      await businessService.deleteOutgoing(id);
      setOutgoings(outgoings.filter(outgoing => outgoing.id !== id));
    } catch (error) {
      console.error('Error deleting outgoing:', error);
      alert('Failed to delete outgoing. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `£${parseFloat(amount || 0).toFixed(2)}`;
  };

  if (loading && outgoings.length === 0) {
    return <div className="loading">Loading outgoings data...</div>;
  }

  if (error && outgoings.length === 0) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="overheads-container">
      <div className="overheads-header">
        <h2>Business Outgoings</h2>
        <p className="help-text">
          Now it's time to enter your business outgoings, don't forget those sneaky ones that come out quarterly or yearly!
        </p>
        
        <div className="warning-box" style={{ 
          backgroundColor: '#fff3cd', 
          border: '1px solid #ffc107', 
          padding: '15px', 
          marginTop: '15px',
          marginBottom: '15px',
          borderRadius: '4px'
        }}>
          <p className="help-text warning" style={{ margin: '0 0 10px 0', fontWeight: 'bold' }}>
            ⚠️ IMPORTANT - Do NOT include the following (they're calculated separately):
          </p>
          <ul style={{ margin: '0', paddingLeft: '25px' }}>
            <li><strong>Staff wages / Payroll</strong> - Enter staff hourly rates in the Staff tab instead</li>
            <li><strong>Stock / Product costs</strong> - These are calculated per service in the Materials column</li>
            <li><strong>Your personal wage</strong> - This is handled on the Income page</li>
          </ul>
          <p className="help-text" style={{ margin: '10px 0 0 0' }}>
            ✅ <strong>DO include:</strong> Rent, utilities, insurance, marketing, equipment, and other fixed business costs.
          </p>
        </div>
        
        {/* Add a note about how these values are used in pricing */}
        <div className="pricing-note">
          <p><strong>Note:</strong> Your total annual outgoings (£{totalAnnualCost.toFixed(2)}) are used to calculate the overhead per minute for your service pricing.</p>
        </div>
      </div>
      
      {outgoings.length === 0 ? (
        <div className="no-data-message">
          <p>No outgoings found. Add your first outgoing below.</p>
        </div>
      ) : (
        <div className="outgoings-table-container">
          <table className="outgoings-table">
            <thead>
              <tr>
                <th>Outgoing</th>
                <th>Cost</th>
                <th>Regularity</th>
                <th>Annual Cost</th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              {outgoings.map((outgoing, index) => {
                const typeId = parseInt(outgoing.type_id);
                const isExcluded = typeId === 17 || typeId === 22;
                
                return (
                  <tr 
                    key={outgoing.id} 
                    className={index % 2 === 0 ? 'even-row' : 'odd-row'}
                    style={isExcluded ? { backgroundColor: '#fff3cd' } : {}}
                  >
                    <td>
                      {outgoing.name}
                      {isExcluded && (
                        <span style={{ 
                          fontSize: '0.85em', 
                          color: '#856404',
                          marginLeft: '8px',
                          fontStyle: 'italic'
                        }}>
                          (not included in pricing)
                        </span>
                      )}
                    </td>
                    <td>
                      <input
                        type="number"
                        value={outgoing.amount || ''}
                        onChange={(e) => handleAmountChange(outgoing.id, e.target.value)}
                        min="0"
                        step="0.01"
                        className="cost-input"
                      />
                    </td>
                    <td>
                      <select
                        value={outgoing.regularity || 'monthly'}
                        onChange={(e) => handleRegularityChange(outgoing.id, e.target.value)}
                        className="regularity-select"
                      >
                        {REGULARITY_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {formatCurrency(calculateAnnualCost(outgoing.amount, outgoing.regularity))}
                      {isExcluded && (
                        <span style={{ 
                          fontSize: '0.85em', 
                          color: '#856404',
                          marginLeft: '8px'
                        }}>
                          ⚠️
                        </span>
                      )}
                    </td>
                    <td>
                      <button
                        className="delete-button"
                        onClick={() => deleteOutgoing(outgoing.id)}
                        aria-label="Delete outgoing"
                      >
                        <i className="fa fa-trash"></i> Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
              <tr className="total-row">
                <td colSpan="3"><strong>Total Annual Outgoings (for pricing):</strong></td>
                <td><strong>{formatCurrency(totalAnnualCost)}</strong></td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      
      <div className="add-outgoing-container">
        <h3>Add New Outgoing</h3>
        <div className="add-outgoing-form">
          <div className="form-group">
            <label htmlFor="outgoing-name">Outgoing Name:</label>
            <input
              type="text"
              id="outgoing-name"
              name="name"
              value={newOutgoing.name}
              onChange={handleInputChange}
              placeholder="e.g. Rent, Insurance, Utilities"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="outgoing-type">Type:</label>
            <select
              id="outgoing-type"
              name="type_id"
              value={newOutgoing.type_id}
              onChange={handleInputChange}
            >
              {EXPENSE_TYPES.map(type => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="outgoing-amount">Amount:</label>
            <input
              type="number"
              id="outgoing-amount"
              name="amount"
              value={newOutgoing.amount}
              onChange={handleInputChange}
              min="0"
              step="0.01"
              placeholder="Amount"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="outgoing-regularity">Regularity:</label>
            <select
              id="outgoing-regularity"
              name="regularity"
              value={newOutgoing.regularity}
              onChange={handleInputChange}
            >
              {REGULARITY_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          
          <button
            type="button"
            className="add-button"
            onClick={addOutgoing}
            disabled={loading}
          >
            {loading ? 'Adding...' : 'Add Outgoing'}
          </button>
        </div>
      </div>
      
      <div className="help-footer">
        <p>Got some you need to add? Use the form above to add custom outgoings.</p>
        <p>Accidentally deleted a row? <button onClick={() => fetchOutgoings()} className="text-button">Refresh the list of outgoings</button>.</p>
      </div>
    </div>
  );
};

export default OverheadsTab;